import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Pentagon, X } from 'lucide';
import { strategyRule } from '@mv/models';
import { lucideIcon } from '../icon.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { inSublocation, saveSublocation, removeSublocation, ringAreaM2 } from '../utils/sublocations.js';
import { api } from '../api.js';

const sameIds = (a = [], b = []) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Het bewerkvenster van één deellocatie: naam, oppervlakte, omschrijving en welke boringen erbij
 * horen. Staat op de projectkaart (rechts over de kaart) en achter 'Beheren' bij het samenstellen
 * van een labopdracht (als venster over de pagina); de ouder bepaalt waar het staat.
 *
 * De strategie (NEN 5740) is hier alleen te zien: die kies je bij het vooronderzoek, omdat het
 * monsterplan en de mengregels ervan afhangen.
 *
 * `borings`: [{ guid, name, lat, lng, sublocationIds }] — lat/lng null zonder coördinaat.
 *
 * Events (bubbelen, composed):
 * - `sublocations-assigned` { changes: { [guid]: sublocationIds } } — toewijzing opgeslagen
 * - `sublocation-overrides` { overrides } — wat nu getoond wordt maar nog niet herladen is
 * - `sublocation-deleted` { id }
 * - `close`
 */
export class SublocationEditor extends LitElement {
  static properties = {
    projectId: { type: String },
    sublocation: { attribute: false },
    sublocations: { attribute: false },
    borings: { attribute: false },
    _form: { state: true },
    _busy: { state: true },
    _error: { state: true },
    _addMode: { state: true },
    _overrides: { state: true },   // boring-id → lijst deellocatie-id's, net toegewezen (zie _assign)
  };

  constructor() {
    super();
    this.sublocations = [];
    this.borings = [];
    this._form = {};
    this._busy = false;
    this._error = '';
    this._addMode = false;
    this._overrides = {};
    this._touched = new Set();
  }

  willUpdate(changed) {
    if (changed.has('sublocation')) {
      const prev = changed.get('sublocation');
      const s = this.sublocation;
      if (s && prev?.id !== s.id) {
        this._addMode = false;
        // Wat je in dit venster uitvinkt blijft zichtbaar tot je een andere deellocatie opent:
        // anders verdwijnt de rij onder je vinger en kun je een misklik niet terugdraaien.
        this._touched = new Set();
        this._error = '';
        this._form = { name: s.name, description: s.description || '', areaM2: s.areaM2 ?? '' };
      }
    }
    // Wat de herladen boringen al bevestigen, hoeft niet meer als override te blijven staan.
    if (changed.has('borings') && Object.keys(this._overrides).length) {
      const byId = new Map((this.borings || []).map((b) => [b.guid, b]));
      const left = Object.fromEntries(Object.entries(this._overrides)
        .filter(([id, v]) => byId.has(id) && !sameIds(byId.get(id).sublocationIds, v)));
      if (Object.keys(left).length !== Object.keys(this._overrides).length) this._setOverrides(left);
    }
  }

  _emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  _setOverrides(o) {
    this._overrides = o;
    this._emit('sublocation-overrides', { overrides: o });
  }

  _idsOf(b) {
    return Object.prototype.hasOwnProperty.call(this._overrides, b.guid) ? this._overrides[b.guid] : (b.sublocationIds || []);
  }

  /** De deellocaties van een boring met `sublocationId` erbij (`on`) of eraf. */
  _withSublocation(b, sublocationId, on) {
    const ids = this._idsOf(b).filter((id) => id !== sublocationId);
    return on ? [...ids, sublocationId] : ids;
  }

  get _located() { return (this.borings || []).filter((b) => b.lat != null && b.lng != null); }

  async _save() {
    const s = this.sublocation;
    if (!s) return;
    const f = this._form;
    this._busy = true;
    this._error = '';
    try {
      // Zonder strategyCode: die blijft wat het vooronderzoek zegt.
      await saveSublocation(this.projectId, s.id, {
        name: f.name, description: f.description,
        areaM2: f.areaM2 === '' ? null : f.areaM2,
      });
    } catch (e) {
      this._error = e.message || 'Opslaan mislukt.';
    } finally {
      this._busy = false;
    }
  }

  /**
   * Alle boringen met coördinaat in het vlak (of binnen 0,5 m van de rand) horen bij deze
   * deellocatie; de rest niet. Hun andere deellocaties blijven staan.
   */
  async _assignFromPolygon() {
    const s = this.sublocation;
    if (!s?.geometry?.length) return;
    const inside = this._located.filter((b) => inSublocation(b.lng, b.lat, s));
    // Een boring zonder coördinaat ligt nergens in; die blijft zoals hij was toegewezen.
    const keep = (this.borings || []).filter((b) => (b.lat == null || b.lng == null) && this._idsOf(b).includes(s.id));
    const wanted = new Set([...inside, ...keep].map((b) => b.guid));
    const touched = [...(this.borings || []).filter((b) => this._idsOf(b).includes(s.id)), ...inside];
    await this._assign(() => api.assignSublocationBorings(s.id, [...wanted]),
      Object.fromEntries(touched.map((b) => [b.guid, this._withSublocation(b, s.id, wanted.has(b.guid))])));
  }

  async _toggle(b, on) {
    const s = this.sublocation;
    if (!s) return;
    this._touched.add(b.guid);
    const ids = this._withSublocation(b, s.id, on);
    await this._assign(() => api.setBoringSublocations(b.guid, ids), { [b.guid]: ids });
  }

  /**
   * Toewijzen: meteen tonen (overrides), dan opslaan, dan de ouder laten herladen. Mislukt het,
   * dan gaan de overrides terug en staat er een melding.
   */
  async _assign(call, changes) {
    const before = this._overrides;
    this._setOverrides({ ...before, ...changes });
    this._error = '';
    try {
      await call();
      this._emit('sublocations-assigned', { changes });
    } catch (e) {
      this._setOverrides(before);
      this._error = e.message || 'Toewijzen mislukt.';
    }
  }

  async _delete() {
    const s = this.sublocation;
    if (!s) return;
    // De boringen zelf blijven staan, en hun andere deellocaties ook.
    const ok = await confirmDialog({
      title: `Deellocatie ${s.name} verwijderen?`,
      message: 'De boringen blijven staan.',
      confirmLabel: 'Verwijderen', danger: true,
    });
    if (!ok) return;
    try {
      await removeSublocation(this.projectId, s.id);
      this._emit('sublocation-deleted', { id: s.id });
      this._emit('sublocations-assigned', { changes: {} });
    } catch (e) {
      this._error = e.message || 'Verwijderen mislukt.';
    }
  }

  render() {
    const s = this.sublocation;
    if (!s) return '';
    const f = this._form;
    const all = this.borings || [];
    const mine = all.filter((b) => this._idsOf(b).includes(s.id));
    // Standaard alleen wat erin zit (plus wat je net uitvinkte); 'toevoegen' toont alles.
    const shown = this._addMode ? all : all.filter((b) => this._idsOf(b).includes(s.id) || this._touched.has(b.guid));
    const polyArea = ringAreaM2(s.geometry);
    const inPoly = s.geometry?.length >= 3 ? this._located.filter((b) => inSublocation(b.lng, b.lat, s)).length : 0;
    const rule = strategyRule(s.strategyCode);
    const field = (label, key, attrs = {}) => html`
      <label class="dl-field"><span>${label}</span>
        <input .value=${String(f[key] ?? '')} placeholder=${attrs.placeholder || ''} maxlength=${attrs.max || 200}
               inputmode=${attrs.numeric ? 'decimal' : 'text'}
               @input=${(e) => { this._form = { ...this._form, [key]: e.target.value }; }}
               @keydown=${(e) => { if (e.key === 'Enter') this._save(); }}>
      </label>`;
    return html`
      <div class="dl-panel" role="dialog" aria-label="Deellocatie ${s.name} bewerken" @keydown=${(e) => { if (e.key === 'Escape') this._emit('close'); }}>
        <div class="dl-panel-head">
          <div>
            <div class="dl-panel-title">${unsafeSVG(lucideIcon(Pentagon, 16))} ${s.name}</div>
            <div class="dl-panel-sub">Deellocatie bewerken · ${mine.length === 1 ? '1 boring' : `${mine.length} boringen`}${polyArea ? ` · vlak ${polyArea.toLocaleString('nl-NL')} m²` : ''}</div>
          </div>
          <button class="dl-close" @click=${() => this._emit('close')} title="Sluiten" aria-label="Sluiten">${unsafeSVG(lucideIcon(X, 16))}</button>
        </div>
        <div class="dl-body">
          <div class="dl-grid">
            ${field('Naam', 'name', { max: 60 })}
            ${field('Oppervlakte (m²)', 'areaM2', { numeric: true, placeholder: polyArea ? `uit vlak: ${polyArea.toLocaleString('nl-NL')}` : '' })}
            <div class="wide">${field('Omschrijving', 'description', { max: 500, placeholder: 'bijv. voormalige tankplaats' })}</div>
            <!-- Alleen-lezen: het monsterplan en de mengregels hangen aan de strategie, dus die
                 wijzig je op één plek, bij het vooronderzoek. -->
            <!-- Een uitgeschakeld veld krijgt zelf geen muisbewegingen; de omhulling wel, dus daar
                 hangt de tooltip aan (meteen, zonder de vertraging van title). -->
            <label class="dl-field wide"><span>Strategie (NEN 5740)</span>
              <div class="dl-vast" data-tip="Aan te passen bij Vooronderzoek" @mousemove=${tipMove} @mouseleave=${tipHide}>
                <select disabled aria-description="Aan te passen bij Vooronderzoek">
                  <option>${s.strategyCode ? `${s.strategyCode}${rule ? ` — ${rule.label}` : ''}` : 'Nog geen strategie'}</option>
                </select>
              </div>
            </label>
          </div>
          ${polyArea && f.areaM2 === '' ? html`
            <button class="dl-link" @click=${() => { this._form = { ...this._form, areaM2: String(polyArea) }; }}>
              Oppervlakte uit vlak overnemen</button>` : ''}
          <div class="dl-actions">
            <button class="dl-save" ?disabled=${this._busy} @click=${() => this._save()}>${this._busy ? 'Opslaan…' : 'Opslaan'}</button>
            <button class="dl-delete" @click=${() => this._delete()}>Deellocatie verwijderen</button>
          </div>
          ${this._error ? html`<div class="dl-error">${this._error}</div>` : ''}

          <div class="dl-section">
            <b>Boringen · ${mine.length}</b>
            <button class="dl-assign ${this._addMode ? 'on' : ''}" @click=${() => { this._addMode = !this._addMode; }}>
              ${this._addMode ? 'Klaar met toevoegen' : '+ Boringen toevoegen'}</button>
          </div>
          ${this._addMode ? html`
            <div class="dl-addbar">
              <span>Vink aan welke boringen bij ${s.name} horen.</span>
              ${s.geometry?.length >= 3 ? html`
                <button class="dl-link" @click=${() => this._assignFromPolygon()}
                        title="Alle boringen met een coördinaat in het vlak (of binnen 0,5 m van de rand) bij deze deellocatie; de rest niet. Andere deellocaties van een boring blijven.">
                  Alles in het vlak (${inPoly})</button>` : ''}
            </div>` : ''}
          ${!this._addMode && !shown.length ? html`<div class="dl-empty">Nog geen boringen in ${s.name}. Kies <b>+ Boringen toevoegen</b>.</div>` : ''}
          <div class="dl-borings">
            ${shown.map((b) => {
              const ids = this._idsOf(b);
              // Ook in andere deellocaties: ter info; aanvinken haalt hem daar niet weg.
              const elsewhere = (this.sublocations || []).filter((x) => x.id !== s.id && ids.includes(x.id)).map((x) => x.name);
              return html`
                <label class="dl-boring">
                  <input type="checkbox" .checked=${ids.includes(s.id)} @change=${(e) => this._toggle(b, e.target.checked)}>
                  <span>${b.name}</span>
                  ${elsewhere.length ? html`<span class="item-dl" title="Hoort ook bij ${elsewhere.join(' en ')}">ook ${elsewhere.join(' + ')}</span>` : ''}
                </label>`;
            })}
          </div>
        </div>
      </div>`;
  }

  static styles = css`
    :host { display:block; }
    .dl-panel { height:100%; box-sizing:border-box; display:flex; flex-direction:column; background:var(--mv-surface); border:1px solid var(--mv-border);
                border-top:3px solid #c2410c; border-radius:12px; box-shadow:0 12px 32px rgba(24,28,50,.18); overflow:hidden; }
    .dl-panel-head { display:flex; align-items:flex-start; gap:10px; padding:14px 16px 12px; border-bottom:1px solid var(--mv-border); }
    .dl-panel-title { display:flex; align-items:center; gap:7px; font-weight:800; font-size:1rem; color:#c2410c; }
    .dl-panel-sub { font-size:.74rem; color:var(--mv-text-muted); margin-top:2px; }
    .dl-close { margin-left:auto; flex:none; display:grid; place-items:center; width:30px; height:30px; border:1px solid var(--mv-border);
                border-radius:8px; background:var(--mv-surface); color:var(--mv-text-muted); cursor:pointer; }
    .dl-close:hover { color:var(--mv-text); border-color:var(--mv-text-muted); }
    .dl-body { flex:1; overflow:auto; padding:14px 16px 16px; display:flex; flex-direction:column; gap:12px; }
    .dl-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px 12px; }
    .dl-grid .wide { grid-column:1 / -1; }
    .dl-field { display:flex; flex-direction:column; gap:4px; font-size:0.72rem; color:var(--mv-text-sub); font-weight:700; }
    .dl-field input, .dl-field select { border:1px solid var(--mv-border); border-radius:7px; padding:7px 9px; font:inherit; font-size:0.84rem;
                                        font-weight:400; color:var(--mv-text); background:var(--mv-surface); }
    .dl-field select:disabled { background:var(--mv-bg); color:var(--mv-text-sub); opacity:1; pointer-events:none; width:100%; }
    .dl-vast { display:flex; cursor:not-allowed; }
    .dl-field input:focus { outline:none; border-color:#c2410c; }
    .dl-link { align-self:flex-start; border:none; background:none; padding:0; font:inherit; font-size:0.72rem; font-weight:600; color:#c2410c; cursor:pointer; }
    .dl-actions { display:flex; gap:8px; align-items:center; }
    .dl-save { background:#c2410c; color:#fff; border:none; border-radius:7px; padding:7px 14px; font:inherit; font-size:0.8rem; font-weight:700; cursor:pointer; }
    .dl-save:disabled { opacity:.5; cursor:default; }
    .dl-delete { border:none; background:none; font:inherit; font-size:0.76rem; font-weight:600; color:var(--mv-text-muted); cursor:pointer; margin-left:auto; }
    .dl-delete:hover { color:var(--mv-danger,#c0392b); }
    .dl-error { color:var(--mv-danger,#c0392b); font-size:0.76rem; }
    .dl-section { display:flex; align-items:center; gap:8px; padding-top:12px; border-top:1px solid var(--mv-border); }
    .dl-section b { font-size:.72rem; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:var(--mv-text-sub); }
    .dl-assign { margin-left:auto; border:1px solid #c2410c; background:var(--mv-surface); color:#c2410c; border-radius:7px; padding:5px 10px; font:inherit; font-size:0.74rem; font-weight:700; cursor:pointer; }
    .dl-assign:hover { background:#fff7f2; }
    .dl-assign.on { background:#c2410c; color:#fff; }
    .dl-addbar { display:flex; align-items:center; gap:10px; padding:8px 10px; border-radius:8px; background:#fff7f2; font-size:.76rem; color:#9a3412; }
    .dl-addbar .dl-link { margin-left:auto; }
    .dl-empty { font-size:.8rem; color:var(--mv-text-muted); padding:6px 2px; }
    .dl-borings { display:grid; grid-template-columns:1fr 1fr; gap:0 10px; }
    .dl-boring { display:flex; align-items:center; gap:7px; padding:4px 4px; border-radius:6px; font-size:0.8rem; color:var(--mv-text); cursor:pointer; min-width:0; }
    .dl-boring:hover { background:var(--mv-surface-alt,#f3f4f6); }
    .item-dl { color:#c2410c; font-weight:600; margin-left:auto; font-size:0.68rem; white-space:nowrap; }
  `;
}

customElements.define('sublocation-editor', SublocationEditor);
